// One row in the employment list: an employer, or a stretch of unemployment.
//
// The card computes its own range and duration from month indices. That is the
// point of it — every mockup round that hand-wrote those strings ended up with a
// range reading backwards or a duration disagreeing with its own dates.

import { ChevronRight } from 'lucide-react'

import {
  formatMonthCount,
  formatMonthRange,
} from 'src/utils/date/formatMonthPeriod'
import { currentMonthIndex } from 'src/utils/date/monthPeriods'

export type EntryKind = 'employment' | 'unemployment'

/** Why a card is asking to be opened. */
export type EntryAttention = 'none' | 'needsAnswers' | 'invalid'

export interface EntryCardProps {
  /** The employer. Unemployment has none, and is labelled as such. */
  name?: string
  kind?: EntryKind
  /** Null when the entry has no dates yet; the card says so instead of a range. */
  startMonth: number | null
  /** Null or undefined runs the entry to the present. */
  endMonth?: number | null
  attention?: EntryAttention
  /** What is outstanding. Defaults to "Validation errors" for an invalid entry. */
  note?: string
  /** Present month, so an ongoing entry has a length. Injected for stable stories. */
  nowMonth?: number
  onOpen: () => void
}

const join = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

/**
 * One background per state, never two.
 *
 * A quiet unemployment card previously took bg-white from here and
 * bg-doubleNickel-gray-50 from the unemployment branch; with no merge helper
 * Tailwind keeps both and stylesheet order picks, so the grey wash that tells
 * unemployment apart from employment might simply not appear.
 */
const TONE: Record<EntryAttention, string> = {
  none: 'border-doubleNickel-gray-300 bg-white',
  needsAnswers: 'border-doubleNickel-warning-300 bg-doubleNickel-warning-50',
  invalid: 'border-doubleNickel-error-200 bg-doubleNickel-error-50',
}

const NOTE_TONE: Record<EntryAttention, string> = {
  none: '',
  needsAnswers: 'text-doubleNickel-warning-700',
  invalid: 'text-doubleNickel-error-700',
}

const CHEVRON_TONE: Record<EntryAttention, string> = {
  none: 'text-doubleNickel-gray-400',
  needsAnswers: 'text-doubleNickel-warning-600',
  invalid: 'text-doubleNickel-error-600',
}

export const EntryCard = ({
  name,
  kind = 'employment',
  startMonth,
  endMonth,
  attention = 'none',
  note,
  nowMonth,
  onOpen,
}: EntryCardProps) => {
  const isUnemployment = kind === 'unemployment'
  const label = isUnemployment ? 'Unemployment' : name
  const end = endMonth ?? nowMonth ?? currentMonthIndex()
  const undated = startMonth == null
  const outstanding =
    attention === 'invalid' ? note ?? 'Validation errors' : note

  return (
    <button
      type="button"
      onClick={onOpen}
      data-attention={attention}
      className={join(
        'flex w-full items-center gap-2.5 rounded-[11px] border p-[10px] text-left shadow-sm',
        isUnemployment && attention === 'none'
          ? 'border-doubleNickel-gray-300 bg-doubleNickel-gray-50'
          : TONE[attention],
        isUnemployment && 'border-dashed'
      )}
    >
      <div className="min-w-0 flex-1">
        <div
          className={join(
            'truncate text-[12.5px] font-semibold',
            isUnemployment
              ? 'text-doubleNickel-gray-700'
              : 'text-doubleNickel-gray-900'
          )}
        >
          {label}
        </div>
        <div className="mt-0.5 text-[11.5px] text-doubleNickel-gray-500">
          {undated ? (
            <span className="font-medium text-doubleNickel-gray-700">
              Dates missing
            </span>
          ) : (
            <>
              <span className="font-medium text-doubleNickel-gray-700">
                {formatMonthRange(startMonth as number, endMonth)}
              </span>
              {' · '}
              {formatMonthCount(end - (startMonth as number) + 1)}
            </>
          )}
        </div>
        {outstanding && (
          <div
            className={join(
              'mt-1 text-[11px] font-medium',
              NOTE_TONE[attention]
            )}
          >
            {outstanding}
          </div>
        )}
      </div>
      <ChevronRight size={16} className={CHEVRON_TONE[attention]} />
    </button>
  )
}
